import Head from 'expo-router/head';

import { listSeedScript } from '@/components/detail/list-seed';
import { cities } from '@/lib/events-data';
import {
  localizedTourDescription,
  localizedTourSeoTitle,
  localizedTourSummary,
  localizedTourTitle,
} from '@todo/core/i18n/tour-content';
import type { Locale } from '@todo/core/i18n/locales';
import {
  SITE_NAME,
  absoluteUrl,
  breadcrumbJsonLd,
  hreflangLinks,
  itemListJsonLd,
  merchantReturnPolicy,
  offerShippingDetails,
  ogLocale,
  richResultImage,
  seoTitle,
  toursCrumb,
  truncate,
} from '@todo/core/seo';
import { prefetchElementId, serializePrefetch } from '@todo/core/static-prefetch';
import { isTourDelisted, type Tour } from '@todo/core/tours';
import { SITE } from '@/site';

// Display name for the supplier a tour is booked through, keyed by
// `tours.source` (set by the sync function — currently only 'viator').
const SOURCE_BRAND: Record<string, string> = { viator: 'Viator' };

// schema.org/Product structured data — rating + price make tour pages eligible
// for rich results. `name` / `description` are the localized copy (falling back
// to the raw Viator text); the rating / price / image come from structured
// fields and don't vary by locale.
function tourJsonLd(tour: Tour, url: string, name: string, description: string | null) {
  const brand = SOURCE_BRAND[tour.source];
  // Viator ships 720x480, which clears Google's 696px floor — but guard it the
  // same way events do so a future source with small thumbnails degrades
  // gracefully instead of shipping an image the validator rejects.
  const image = richResultImage(tour.image_url);
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    url,
    // `sku` and `brand` are what Google uses to tell one product listing from
    // another; without them every tour looks like an unidentified product.
    // `source_id` is the supplier's own product code and is unique per source.
    sku: tour.source_id,
    ...(brand ? { brand: { '@type': 'Brand', name: brand } } : {}),
    ...(image ? { image: [image] } : {}),
    ...(description ? { description: truncate(description, 500) } : {}),
    ...(tour.rating != null && tour.review_count
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: tour.rating,
            reviewCount: tour.review_count,
          },
        }
      : {}),
    ...(tour.from_price != null
      ? {
          offers: {
            '@type': 'Offer',
            price: tour.from_price,
            priceCurrency: tour.currency ?? SITE.currency,
            availability: 'https://schema.org/InStock',
            ...(tour.booking_url ? { url: tour.booking_url } : {}),
            // Merchant-listing fields Google flags as missing on any priced
            // Product offer — electronic voucher, so free "shipping" and no
            // returns. See the helpers in @todo/core/seo.
            shippingDetails: offerShippingDetails(tour.currency ?? SITE.currency),
            hasMerchantReturnPolicy: merchantReturnPolicy(),
          },
        }
      : {}),
  };
}

// Per-tour <Head>, shared by the native and web variants of
// app/[lang]/tours/[id] so the SEO markup has a single source of truth.
//
// `related` / `relatedSeedKey` bake the "more tours" block's data into the head
// so it's crawlable and hydrates cleanly — same mechanism as EventHead.
export function TourHead({
  tour,
  locale,
  related,
  relatedSeedKey,
}: {
  tour: Tour;
  locale: Locale;
  related?: Tour[];
  relatedSeedKey?: string;
}) {
  const cityName = tour.city_id
    ? (cities.find((c) => c.id === tour.city_id)?.name ?? tour.city_id)
    : null;
  const routePath = `tours/${tour.id}`;
  const canonical = absoluteUrl(locale, routePath);
  const title = localizedTourTitle(tour, locale);
  const description = localizedTourDescription(tour, locale);
  // Meta uses the localized ≤160-char summary first (already capped), else a
  // truncated localized description, else a generated fallback sentence —
  // mirrors event-head.tsx.
  const metaDescription =
    localizedTourSummary(tour, locale) ??
    truncate(
      description ??
        `${title}${cityName ? ` in ${cityName}` : ' on the French Riviera'} — book this experience on ${SITE_NAME}.`,
    );

  return (
    <Head key={`${locale}:${tour.id}`}>
      {/* Viator product names run to 128 chars, so the <title> uses the
          generated SERP-sized seoTitle (falling back to the full name, which
          seoTitle() then trims). The <h1> keeps the full name. */}
      <title>{seoTitle(localizedTourSeoTitle(tour, locale))}</title>
      <meta name="description" content={metaDescription} />
      {/* Delisted tours: kept out of the index (and sitemap.xml) until they're
          purged, but their links stay followed — same as delisted boats. */}
      {isTourDelisted(tour) ? <meta name="robots" content="noindex, follow" /> : null}
      <link rel="canonical" href={canonical} />
      {hreflangLinks(routePath).map((link) => (
        <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
      ))}
      <meta property="og:type" content="product" />
      <meta property="og:locale" content={ogLocale(locale)} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonical} />
      {tour.image_url ? <meta property="og:image" content={tour.image_url} /> : null}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metaDescription} />
      {tour.image_url ? <meta name="twitter:image" content={tour.image_url} /> : null}
      {/* No Product/Offer for a delisted tour — it can't be booked any more. */}
      {isTourDelisted(tour) ? null : (
        <script type="application/ld+json">
          {JSON.stringify(tourJsonLd(tour, canonical, title, description))}
        </script>
      )}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbJsonLd(locale, [toursCrumb(locale), { name: title }]))}
      </script>
      {related && related.length > 0 ? (
        <script type="application/ld+json">
          {JSON.stringify(
            itemListJsonLd(
              related.map((r) => ({
                name: localizedTourTitle(r, locale),
                url: absoluteUrl(locale, `tours/${r.id}`),
              })),
              canonical,
            ),
          )}
        </script>
      ) : null}
      {/* Baked record so the first client render matches this static HTML and
          hydration doesn't mismatch (#418). */}
      <script id={prefetchElementId('tour', tour.id)} type="application/json">
        {serializePrefetch(tour)}
      </script>
      {/* Baked list seed for the "more tours" block — reuses the same
          city/region seed the block reads, so its cards hydrate without a
          refetch. Emitted last so it never displaces the singular tags above. */}
      {relatedSeedKey ? listSeedScript(relatedSeedKey) : null}
    </Head>
  );
}
